import React, { useState } from 'react';
import styled from 'styled-components';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Seo from '../../components/seo/Seo';
import { useTranslation } from 'react-i18next';

const Layout = styled.div`
  min-height: 100vh;
  color: var(--ink);
  background: var(--paper);
  padding: 160px 0 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 900px) {
    padding: 130px 0 64px;
  }
`;

const PortalShell = styled.div`
  position: relative;
  width: calc(100% - var(--gutter) * 2);
  max-width: 1248px;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: 3rem;
  padding: 3.2rem;
  border-radius: 12px;
  background: var(--surface);
  border: 1px solid var(--line);
  box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
  backdrop-filter: blur(12px);
  z-index: 1;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 2rem;
  }
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  z-index: 1;
`;

const Kicker = styled.p`
  text-transform: uppercase;
  letter-spacing: 0.32rem;
  font-size: 1.4rem;
  color: var(--muted);
  margin: 0;
`;

const Title = styled.h1`
  font-size: clamp(2.4rem, 3.8vw, 4.1rem);
  line-height: 1.1;
  font-weight: 400;
  margin: 0;
  color: var(--ink);
`;

const Subtitle = styled.p`
  font-size: 1.6rem;
  line-height: 1.7;
  color: var(--muted);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const Badge = styled.span`
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  font-size: 1.4rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink);
  border: 1px solid var(--line);
  background: var(--surface);
`;

const LoginCard = styled.div`
  @media (max-width: 600px) {
    padding: 1.6rem;
  }
  border-radius: 1.8rem;
  padding: 2.4rem;
  background: var(--surface);
  border: 1px solid var(--line);
  box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
  z-index: 1;
`;

const LoginTitle = styled.h2`
  font-size: 1.8rem;
  margin: 0 0 0.5rem;
`;

const LoginSub = styled.p`
  margin: 0 0 1.6rem;
  color: var(--muted);
  font-size: 1.4rem;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  font-size: 1.4rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--muted);
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: var(--paper);
  color: var(--ink);
  font-size: 1.6rem;

  &:focus {
    border-color: var(--ink);
    box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
    outline: none;
  }
`;

const StyledFieldError = styled.div`
  margin-top: 0.4rem;
  font-size: 1.4rem;
  color: var(--muted);
`;

const StyledFormError = styled.div`
  color: var(--muted);
  margin-bottom: 0.6rem;
  text-align: left;
  font-size: 1.4rem;
`;

const LoadingIndicator = styled.div`
  border: 2px solid var(--line);
  border-radius: 50%;
  border-top: 2px solid var(--ink);
  width: 1.6rem;
  height: 1.6rem;
  animation: spin 1s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PrimaryButton = styled.button`
  margin-top: 0.6rem;
  padding: 1rem 1.5rem;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--ink);
  color: var(--paper);
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 30px rgba(32, 33, 34, 0.04);
  }

  min-height: 48px;
`;

const SupportRow = styled.div`
  margin-top: 1.2rem;
  font-size: 1.4rem;
  color: var(--muted);

  a {
    color: var(--ink);
    text-decoration: none;
  }
`;

export const ClientLogin = () => {
  const [showSpinner, setShowSpinner] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState('');

  const { i18n } = useTranslation('common');
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  const copy =
    lang === 'fr'
      ? {
          kicker: 'Portail client',
          title: 'Accès sécurisé à votre espace.',
          subtitle:
            "Utilisez les identifiants fournis par votre responsable d'engagement.",
          badges: ['Espace', 'Livrables', 'Suivi', 'Support'],
          loginTitle: 'Connexion',
          loginSub:
            'L’accès est réservé aux clients actifs et collaborateurs autorisés.',
          labels: {
            email: 'Courriel',
            password: 'Mot de passe',
          },
          button: 'Se connecter',
          help: 'Besoin d’accès?',
          helpLink: 'Contactez-nous',
          canonical: `/${lang}/app/login`,
        }
      : {
          kicker: 'Client Portal',
          title: 'Secure access to your workspace.',
          subtitle: 'Use the credentials issued by your engagement lead.',
          badges: ['Workspace', 'Deliverables', 'Updates', 'Support'],
          loginTitle: 'Sign in',
          loginSub: 'Access is reserved for active clients and collaborators.',
          labels: {
            email: 'Email',
            password: 'Password',
          },
          button: 'Log in',
          help: 'Need access?',
          helpLink: 'Contact us',
          canonical: `/${lang}/app/login`,
        };

  const contactPath = lang === 'fr' ? '/fr/contact' : '/en/contact';

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },

    validationSchema: yup.object({
      email: yup.string().email().required(),
      password: yup.string().min(6).max(20).required(),
    }),

    onSubmit: async () => {
      setShowSpinner(true);
      setFormErrorMessage('');
      try {
        // auth flow handled elsewhere
      } catch (err) {
        setFormErrorMessage(
          lang === 'fr'
            ? 'Impossible de se connecter. Veuillez réessayer.'
            : 'There was an error logging in. Please try again.',
        );
      }

      setShowSpinner(false);
    },
  });

  return (
    <Layout>
      <Seo
        title="Client Portal Login | SOR SEO"
        description="Secure sign-in to access your SOR SEO client dashboard and project deliverables."
        canonicalPath={copy.canonical}
        robots="noindex,nofollow"
      />
      <PortalShell>
        <Intro>
          <Kicker>{copy.kicker}</Kicker>
          <Title>{copy.title}</Title>
          <Subtitle>{copy.subtitle}</Subtitle>
          <BadgeRow>
            {copy.badges.map((badge) => (
              <Badge key={badge}>{badge}</Badge>
            ))}
          </BadgeRow>
        </Intro>

        <LoginCard>
          <LoginTitle>{copy.loginTitle}</LoginTitle>
          <LoginSub>{copy.loginSub}</LoginSub>
          <Form onSubmit={formik.handleSubmit}>
            {formErrorMessage && (
              <StyledFormError>{formErrorMessage}</StyledFormError>
            )}

            <Field>
              {copy.labels.email}
              <FieldInput
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.email && formik.errors.email && (
                <StyledFieldError>{formik.errors.email}</StyledFieldError>
              )}
            </Field>

            <Field>
              {copy.labels.password}
              <FieldInput
                type="password"
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.password && formik.errors.password && (
                <StyledFieldError>{formik.errors.password}</StyledFieldError>
              )}
            </Field>

            {showSpinner ? (
              <LoadingIndicator />
            ) : (
              <PrimaryButton type="submit">{copy.button}</PrimaryButton>
            )}
          </Form>
          <SupportRow>
            {copy.help} <a href={contactPath}>{copy.helpLink}</a>
          </SupportRow>
        </LoginCard>
      </PortalShell>
    </Layout>
  );
};

export default ClientLogin;
